/**
 * Chat Pop-Out Feature
 *
 * Standalone chat view for the pop-out window.
 * Shows only the chat interface without the task list or other UI elements.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Loader2, Sparkles, Eraser } from 'lucide-react';
import clsx from 'clsx';
import { toast } from 'sonner';
import { useChatSyncContext } from '../../contexts/ChatSyncContext';
import { MarkdownContent, StreamingMarkdownContent } from '../../components/MarkdownContent';
import { ChatCardRenderer } from '../../components/ChatCardRenderer';
import type { ChatCard } from '../../types';

export function ChatPopOut() {
  const [input, setInput] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const {
    messages,
    isLoading,
    streamingContent,
    toolStatus,
    sendMessageStream,
    clearMessages,
    chatContext,
  } = useChatSyncContext();

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 100)}px`;
    }
  }, [input]);

  // Scroll to bottom when new messages arrive or streaming content updates
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages, streamingContent]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const message = input.trim();
    setInput('');
    await sendMessageStream(message);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleCardAction = useCallback(
    (action: NonNullable<ChatCard['actions']>[number]) => {
      if (action.actionType === 'share') {
        const text = action.metadata?.text ?? '';
        if (navigator.share) {
          navigator.share({ text }).catch(() => {});
        } else {
          navigator.clipboard.writeText(text).then(() => {
            toast.success('Copied to clipboard!');
          }).catch(() => {});
        }
      } else if (action.url) {
        window.open(action.url, '_blank', 'noopener,noreferrer');
      }
    },
    []
  );

  return (
    <div className="h-screen flex flex-col bg-surface-raised-light dark:bg-surface-raised-dark">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 bg-surface-base-light dark:bg-surface-base-dark border-b border-border-subtle-light dark:border-border-subtle-dark">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center">
            <Sparkles className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
            Chat with Danny
          </span>
        </div>
        {messages.length > 0 && (
          <button
            onClick={clearMessages}
            className="p-1.5 text-text-tertiary-light dark:text-text-tertiary-dark hover:text-text-secondary-light dark:hover:text-text-primary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark rounded transition-colors"
            aria-label="Clear conversation"
            title="Clear conversation"
          >
            <Eraser className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Context indicator */}
      {chatContext && (chatContext.currentViewName || chatContext.selectedTaskContent) && (
        <div className="shrink-0 px-3 py-1.5 bg-primary-50 dark:bg-primary-900/20 border-b border-primary-100 dark:border-primary-800/30 text-xs text-primary-700 dark:text-primary-300 truncate">
          {chatContext.currentViewName && (
            <span>View: {chatContext.currentViewName}</span>
          )}
          {chatContext.currentViewName && chatContext.selectedTaskContent && (
            <span className="mx-1.5 text-primary-300 dark:text-primary-600">•</span>
          )}
          {chatContext.selectedTaskContent && (
            <span>Task: {chatContext.selectedTaskContent}</span>
          )}
        </div>
      )}

      {/* Messages */}
      <div
        ref={chatContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-3"
      >
        {messages.length === 0 && !isLoading && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-text-tertiary-light dark:text-text-tertiary-dark">
              <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Ask Danny anything...</p>
            </div>
          </div>
        )}
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={clsx(
              'flex',
              msg.role === 'user' ? 'justify-end' : 'justify-start'
            )}
          >
            <div
              className={clsx(
                'max-w-[85%] rounded-lg px-3 py-2 text-sm',
                msg.role === 'user'
                  ? 'bg-primary-700 text-white'
                  : 'bg-surface-base-light dark:bg-surface-hover-dark text-text-primary-light dark:text-text-primary-dark'
              )}
            >
              {msg.role === 'user' ? (
                <p className="whitespace-pre-wrap">{msg.content}</p>
              ) : (
                <MarkdownContent content={msg.content} />
              )}
              {msg.actions && msg.actions.length > 0 && (
                <div className="mt-2 pt-2 border-t border-border-subtle-light/20 dark:border-border-hover-dark/20 space-y-1">
                  {msg.actions.map((action, i) => (
                    <p key={i} className="text-xs opacity-75">
                      ✓ {action.description}
                    </p>
                  ))}
                </div>
              )}
              {msg.cards && msg.cards.map((card, i) => (
                <ChatCardRenderer key={i} card={card} onAction={handleCardAction} />
              ))}
            </div>
          </div>
        ))}
        {/* Streaming content or tool status */}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-surface-base-light dark:bg-surface-hover-dark rounded-lg px-3 py-2 text-sm text-text-primary-light dark:text-text-primary-dark">
              {toolStatus ? (
                <div className="flex items-center gap-2 text-text-secondary-light dark:text-text-secondary-dark">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{toolStatus}</span>
                </div>
              ) : streamingContent ? (
                <StreamingMarkdownContent content={streamingContent} />
              ) : (
                <Loader2 className="w-4 h-4 animate-spin text-text-secondary-light dark:text-text-secondary-dark" />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Input Area */}
      <form
        onSubmit={handleSubmit}
        className="flex-shrink-0 p-3 border-t border-border-subtle-light dark:border-border-subtle-dark bg-surface-raised-light dark:bg-surface-raised-dark"
      >
        <div className="flex items-center justify-center gap-2 bg-surface-base-light dark:bg-surface-base-dark rounded-xl border border-border-subtle-light dark:border-border-subtle-dark focus-within:border-primary-300 dark:focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-100 dark:focus-within:ring-primary-900/50 transition-all">
          {/* Danny Icon */}
          <div className="flex-shrink-0 p-2.5">
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center">
              <Sparkles className="w-3.5 h-3.5 text-white" />
            </div>
          </div>

          {/* Textarea */}
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask Danny anything..."
            rows={1}
            className="chat-input flex-1 py-2.5 pr-2 bg-transparent resize-none focus:outline-none text-sm text-text-primary-light dark:text-text-primary-dark placeholder:text-text-tertiary-light dark:placeholder:text-text-tertiary-dark"
            disabled={isLoading}
          />

          {/* Send Button */}
          <button
            type="submit"
            disabled={!input.trim() || isLoading}
            className={clsx(
              'flex-shrink-0 p-2.5 rounded-lg transition-colors',
              input.trim() && !isLoading
                ? 'text-primary-500 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30'
                : 'text-text-tertiary-light dark:text-text-tertiary-dark'
            )}
            aria-label="Send message"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
